// Retken tuonti tiedostosta (GPX, TCX, FIT). Urheilusovelluksella tallennetun
// lenkin voi tuoda jälkikäteen: ensin esikatselu, sitten käyttäjä valitsee
// jäljet ja kulkutavan. Mitään ei tallenneta ennen "Tuo valitut" -painiketta.

import { useEffect, useState } from "preact/hooks";

import { toBase64 } from "../lib/base64.js";
import { clock, day, kmValue } from "../lib/format.js";
import { MODES, isEligible, modeLabel } from "../lib/modes.js";
import { CompanionPicker } from "./PlayerViews.jsx";
import { Sheet } from "./Sheet.jsx";

const ACCEPT = [".gpx", ".tcx", ".fit", "application/gpx+xml", "application/vnd.garmin.tcx+xml", "application/vnd.ant.fit", "application/xml", "text/xml", "application/octet-stream"].join(",");

/**
 * initial: { name, b64 } kun tiedosto tulee Androidin Jaa-valikosta (lib/shareImport.js). Silloin esikatselu
 * haetaan heti avattaessa; muuten käyttäjä valitsee tiedoston itse.
 */
export function ImportSheet({ app, onClose, initial = null }) {
  const [file, setFile] = useState(null); // { name, b64 }
  const [tracks, setTracks] = useState(null); // esikatselu + käyttäjän valinnat
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [withCompanions, setWithCompanions] = useState([]);

  /** Hakee esikatselun palvelimelta. Käyttäjän valitsema ja Jaa-valikosta tullut tiedosto kulkevat samaa polkua. */
  const load = async (name, b64) => {
    setError(null);
    setTracks(null);
    setBusy(true);
    try {
      const { tracks: preview } = await app.api.previewImport(name, b64);
      setFile({ name, b64 });
      setTracks(
        preview.map((t) => ({
          ...t,
          // Oletuksena ei valita jälkeä, joka on jo tuotu tai menee päällekkäin
          // olemassa olevan retken kanssa: sama lenkki tuplaisi kilometrit.
          selected: !t.already_imported && t.overlaps.length === 0,
          mode: t.suggested_mode ?? "walk",
          chosenName: t.name,
        })),
      );
    } catch (e) {
      setError(e.message);
    }
    setBusy(false);
  };

  const choose = async (f) => {
    if (!f) return;
    setBusy(true);
    try {
      await load(f.name, toBase64(await f.arrayBuffer()));
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };

  // Jaettu tiedosto esikatsellaan heti, kun näkymä avautuu (tai kun uusi tiedosto jaetaan auki olevaan näkymään).
  useEffect(() => {
    if (initial) load(initial.name, initial.b64);
  }, [initial]);

  const update = (index, patch) => setTracks(tracks.map((t) => (t.index === index ? { ...t, ...patch } : t)));
  const selected = tracks?.filter((t) => t.selected) ?? [];

  const doImport = async () => {
    setBusy(true);
    const ok = await app.importTracks(
      file.name,
      file.b64,
      selected.map((t) => ({ index: t.index, mode: t.mode, name: t.chosenName.trim() || null })),
      withCompanions,
    );
    setBusy(false);
    if (ok) onClose();
  };

  return (
    <Sheet title="Tuo retki tiedostosta" onClose={onClose}>
      <p class="hint">
        GPX, TCX tai FIT esimerkiksi Stravasta, Sports Trackerista, Garminilta, Polarilta tai Suuntolta.
        Tuotu retki saa samat ruudut ja palkinnot kuin tallennettu, kun kulkutapa on lihasvoimaa.
      </p>
      <label class="btn btn-block file-btn">
        {file ? `Vaihda tiedosto (${file.name})` : "Valitse tiedosto"}
        {/* Android muuntaa päätteet MIME-tyypeiksi eikä tunne .fit- ja .tcx-päätteitä:
            yleiset tyypit varmistavat, että kellon tiedostot näkyvät valitsimessa. */}
        <input type="file" class="sr-only" accept={ACCEPT} onChange={(e) => choose(e.currentTarget.files[0])} />
      </label>
      {busy && <p class="s" role="status">Luetaan…</p>}
      {error && <div class="notice warn" role="alert">{error}</div>}

      {tracks?.map((t) => (
        <div class={`import-track ${t.selected ? "on" : ""}`} key={t.index}>
          <label class="check">
            <input type="checkbox" checked={t.selected} disabled={t.already_imported} onChange={() => update(t.index, { selected: !t.selected })} />
            <span>{day(t.start)} klo {clock(t.start)}–{clock(t.end)}, {kmValue(t.distance_km)}</span>
          </label>
          {t.already_imported && <p class="s">Tämä jälki on jo tuotu.</p>}
          {t.overlaps.length > 0 && (
            <div class="notice warn">
              Samaan aikaan on jo retki {t.overlaps.map((o) => o.name || "nimetön").join(", ")}. Tuo vain, jos kyse on eri
              lenkistä: muuten kilometrit lasketaan kahdesti. Ruutuja ja pisteitä ei tule kahdesti, ja ne jäävät
              aiemmalle retkelle.
            </div>
          )}
          {t.selected && (
            <>
              <div class="field">
                <label for={`imp-name-${t.index}`}>Nimi</label>
                <input id={`imp-name-${t.index}`} value={t.chosenName} maxLength={120} onInput={(e) => update(t.index, { chosenName: e.currentTarget.value })} />
              </div>
              <div class="field">
                <label for={`imp-mode-${t.index}`}>Kulkutapa{t.activity ? ` (tiedostossa: ${t.activity})` : ""}</label>
                <select id={`imp-mode-${t.index}`} value={t.mode} onChange={(e) => update(t.index, { mode: e.currentTarget.value })}>
                  {MODES.map((m) => <option key={m.id} value={m.id}>{m.label}{m.eligible ? "" : " (siirtymä)"}</option>)}
                </select>
              </div>
              {!isEligible(t.mode) && <p class="s">{modeLabel(t.mode)} on siirtymä: retki tallentuu, mutta ei kerrytä peliä.</p>}
            </>
          )}
        </div>
      ))}

      {tracks && selected.length > 0 && <CompanionPicker app={app} value={withCompanions} onChange={setWithCompanions} />}

      {tracks && (
        <div class="sheet-actions">
          <button class="btn btn-rasti btn-block" disabled={busy || selected.length === 0} onClick={doImport}>
            {selected.length === 0 ? "Valitse tuotava retki" : selected.length === 1 ? "Tuo valittu retki" : `Tuo ${selected.length} retkeä`}
          </button>
          <button class="btn btn-block" onClick={onClose}>Peruuta</button>
        </div>
      )}
    </Sheet>
  );
}
